/**
 * Save a generated file to the user's device.
 *
 * Why this is not just `<a download>`: iOS Safari ignores the `download`
 * attribute on cross-origin URLs. Chatter's outputs are presigned S3 links, so
 * the attribute does nothing there — tapping it navigates to the video and
 * plays it, with no way to keep it. That is the "I can't actually download"
 * case.
 *
 * The Web Share API is the route that works on iOS: sharing a File opens the
 * system share sheet, which offers Save to Files, Save to Photos, AirDrop and
 * the rest. It requires a real File in memory, so the asset is fetched first,
 * and it must be called inside the user gesture that triggered it.
 */

export type DownloadResult = "shared" | "downloaded" | "cancelled";

/** iOS/iPadOS, including iPadOS 13+ which reports itself as a Mac. */
export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/** Fall back to an anchor click — correct everywhere the attribute is honoured. */
function anchorDownload(url: string, filename: string): DownloadResult {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return "downloaded";
}

export async function saveFile(
  url: string,
  filename: string,
  mimeType: string,
): Promise<DownloadResult> {
  // Only worth fetching the bytes if sharing files is actually supported.
  const canShareFiles =
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function";

  if (canShareFiles) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
      const file = new File([await res.blob()], filename, { type: mimeType });

      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
          return "shared";
        } catch (err) {
          // Dismissing the sheet rejects with AbortError. That is a choice,
          // not a failure — do not fall through and start a second download.
          if (err instanceof DOMException && err.name === "AbortError") {
            return "cancelled";
          }
          throw err;
        }
      }
    } catch (err) {
      // CORS, memory pressure, an unsupported type: fall back rather than
      // leaving the user with nothing.
      console.warn("Share sheet unavailable, falling back to download:", err);
    }
  }

  return anchorDownload(url, filename);
}
